import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import BrandLogo from "../components/BrandLogo";
import { trackEvent, trackPageView } from "../lib/analytics";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, googleLogin, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    trackPageView("login_page_view", { path: "/login" });
  }, []);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "programmer") navigate("/programmer/finance");
      else if (user.role === "employee") navigate("/admin/users");
      else if (user.role === "consultant") navigate("/consultant");
      else if (user.role === "accountant") navigate("/accountant");
      else navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    trackEvent("login_submit", { path: "/login", metadata: { method: "email" } });
    
    try {
      const data = await login(email, password);
      trackEvent("login_success_client", { path: "/login", metadata: { method: "email", role: data.role } });
      toast.success("Login realizado com sucesso!");
      
      if (data.role === "admin") navigate("/admin");
      else if (data.role === "programmer") navigate("/programmer/finance");
      else if (data.role === "employee") navigate("/admin/users");
      else if (data.role === "consultant") navigate("/consultant");
      else if (data.role === "accountant") navigate("/accountant");
      else navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      trackEvent("login_failure", {
        path: "/login",
        metadata: { method: "email", reason: error.response?.data?.detail || "unknown" },
      });
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-navy mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar ao início
        </Link>
        
        <Card className="border-0 shadow-xl brand-surface">
          <CardHeader className="text-center pb-2">
            <BrandLogo size="lg" className="mx-auto mb-4" />
            <CardTitle className="font-serif text-2xl text-navy">Bem-vindo de volta</CardTitle>
            <CardDescription>Entre na sua conta para acessar seus diagnósticos e relatórios</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-navy hover:bg-navy/90"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            
            <div className="my-6 flex items-center gap-4">
              <Separator className="flex-1" />
              <span className="text-sm text-slate-500">ou</span>
              <Separator className="flex-1" />
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                trackEvent("login_submit", { path: "/login", metadata: { method: "google" } });
                googleLogin();
              }}
              data-testid="google-login-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar com Google
            </Button>
            
            <p className="text-center mt-6 text-sm text-slate-600">
              Não tem uma conta?{" "}
              <Link to="/register" className="text-navy font-medium hover:underline">
                Criar conta
              </Link>
            </p>

            <p className="text-center mt-3 text-sm text-slate-600">
              É consultor parceiro?{" "}
              <Link to="/consultor-parceiro/cadastro" className="text-navy font-medium hover:underline">
                Fazer cadastro
              </Link>
            </p>
            <p className="text-center mt-2 text-sm text-slate-600">
              É contador parceiro?{" "}
              <Link to="/contador-parceiro/cadastro" className="text-navy font-medium hover:underline">
                Fazer cadastro
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
